import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ContractStepTypeEnum } from "@/constants/enums/StepTypeEnum";
import { archiveContract, postCreateContract, useGetContractToRenew } from "@/services/Contracts";
import { useGetPaymentOptions, useGetPaymentOptionsByCompanyId } from "@/services/Settings";
import { useGetCompanySpecificProductById, useGetCompanySpecificServices } from "@/services/Company";
import { useGetContractDeviationTypes } from "@/services/ContractDeviations";
import { useAuth } from "@/contexts/AuthContext";
import { CreateNewContractPlaceholderObj, ConvertFormToContract, CreateContractWizard } from "./helpers/helpers";
import type { CompanySpecificService, CreateContract, MeasureUnitValue } from "@/types";
import { useGetCreateOrderValues } from "@/services/Orders";
import { useCreateContractWizardStore } from "@/stores/createContractWizardStore";
import { useStepperStore } from "@/stores/stepperStore";
import { useIndexedRef } from "@/hooks/useIndexedRef";
import { translateAmount } from "@/utils/Contracts";
import { useNavigate } from "react-router-dom";
import Modal, { ModalCTAs } from "../../molecules/Modal";

import CTA from "@/components/atoms/CTA";
import ContractDetails from "./Steps/ContractDetails";
import ContractSettings from "./Steps/ContractSettings";
import Transport from "./Steps/Transport";
import Payment from "./Steps/Payment";
import Attachments from "./Steps/Attachments";
import Stepper from "../../molecules/Stepper";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Deviations from "./Steps/Deviations";
import Tooltip from "@/components/atoms/Tooltip";
import MeasureUnit from "@/constants/enums/MeasureUnit";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import styles from "./styles.module.css";
import ModalHeader from "@/components/atoms/ModalHeader";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { useIsWhiteLabel } from "@/services/WhiteLabel";
import PlatformServiceNameEnum from "@/constants/enums/PlatformServiceNameEnum";

const findTradingPartner = (buyers: number[], sellers: number[], currentCompanyId: number): number =>
  buyers.find((buyer) => buyer !== currentCompanyId) || sellers.find((seller) => seller !== currentCompanyId)!;

export default function CreateContract() {
  const isOpen = useCreateContractWizardStore((s) => s.isOpen);
  const contractId = useCreateContractWizardStore((s) => s.contractId);
  const close = useCreateContractWizardStore((s) => s.close);
  const taskId = useCreateContractWizardStore((s) => s.taskId);
  const isArchived = useCreateContractWizardStore((s) => s.isArchived);
  const currentStep = useStepperStore((s) => s.currentStep);
  const setCurrentStep = useStepperStore((s) => s.goTo);
  const next = useStepperStore((s) => s.next);
  const prev = useStepperStore((s) => s.prev);
  const steps = useStepperStore((s) => s.steps);
  const setSteps = useStepperStore((s) => s.setSteps);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createContractValues, setCreateContractValues] = useState<CreateContractWizard>(
    CreateNewContractPlaceholderObj
  );

  useEffect(() => {
    const totalSteps = 6;
    const defaultSteps = 3;

    if (isOpen) {
      setSteps(totalSteps);
    }

    return () => {
      setSteps(defaultSteps);
    };
  }, [isOpen, setSteps]);

  const queryClient = useQueryClient();
  const formSections = useRef<(HTMLFormElement | null)[]>([]);
  const { currentCompany, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: renewedContract } = useGetContractToRenew(contractId as number, {
    enabled: !!contractId,
  });

  const { data: createOrderValues } = useGetCreateOrderValues();

  const { data: contractProduct } = useGetCompanySpecificProductById(
    renewedContract?.companySpecificProductId as number,
    {
      enabled: !!renewedContract?.companySpecificProductId,
    }
  );
  const { data: paymentOptions } = useGetPaymentOptions();

  const { data: paymentDefaultsForCompany } = useGetPaymentOptionsByCompanyId(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });
  const { data: companySpecificServicesDB } = useGetCompanySpecificServices(
    currentCompany?.companyId as number,
    undefined,
    {
      enabled: !!currentCompany?.companyId,
    }
  );
  const { data: contractDeviations } = useGetContractDeviationTypes(currentCompany?.companyId as number, false, {
    enabled: !!currentCompany?.companyId,
  });

  const { isWhiteLabel } = useIsWhiteLabel();

  // Renewing produces a new contract, so factoring must not carry over on a white-label
  // domain. This is the fallback path: normally Payment's defaults effect replaces
  // selectedServices entirely, but it is skipped if the payment options request fails.
  const whiteLabelBlockedServiceId = isWhiteLabel
    ? paymentOptions?.settings.find((s) => s.name === PlatformServiceNameEnum.FACTORING)?.id
    : undefined;

  useEffect(() => {
    if (!renewedContract || !currentCompany?.companyId) return;
    if (!companySpecificServicesDB || !paymentOptions) return;
    const partner = findTradingPartner(
      renewedContract.contractBuyers,
      renewedContract.contractSellers,
      currentCompany?.companyId
    );

    const convertedObj: CreateContractWizard = {
      ...renewedContract,
      agreedBuyerPricePrUnit: Math.abs(renewedContract.agreedBuyerPricePrUnit).toString(),
      agreedSellerPricePrUnit: Math.abs(renewedContract.agreedSellerPricePrUnit).toString(),
      companySpecificServices: (renewedContract.companySpecificServices ?? [])
        .map((service) =>
          companySpecificServicesDB?.find((specificService) => specificService.id === service.companySpecificServiceId)
        )
        .filter((s): s is CompanySpecificService => !!s),
      contractManagerId: renewedContract.contractManagerId ?? (user?.id as number),
      description: renewedContract.description,
      invoiceDueDateId: paymentOptions?.dueDates?.find((option) => option.id === renewedContract.invoiceDueDateId) ??
        paymentOptions?.dueDates?.[0] ?? { id: 1, days: 7 },
      invoiceInformation: renewedContract.invoiceInformation ?? "",
      invoiceIntervalId: renewedContract.invoiceIntervalId,
      name: renewedContract.name,
      priceUnitId: renewedContract.priceUnitId,
      selectedServices: renewedContract.selectedServices.filter((id) => id !== whiteLabelBlockedServiceId),
      tradeAtOwnRisk: false,
      useTransportServices: false,
      amount:
        renewedContract && renewedContract.amount && renewedContract.amountUnitId
          ? translateAmount(renewedContract.amountUnitId, renewedContract.amount.toString())
          : "0",
      amountUnitId: renewedContract.amountUnitId ?? 1,
      comment: renewedContract.comment ?? "",
      contractDate: renewedContract.contractDate ?? new Date(),
      contractStartDate: renewedContract.contractStartDate ?? new Date(),
      contractEndDate: renewedContract.contractEndDate ?? null,
      companySpecificProductId: renewedContract.companySpecificProductId,
      tradingPartner: partner,
      companySpecificProductMeasureUnit:
        (createOrderValues?.units.find((unit) => unit.id === renewedContract.amountUnitId)
          ?.value as MeasureUnitValue) || MeasureUnit.KG,
      contractEndType: renewedContract.contractEndDate ? "DATEPERIOD" : "AMOUNT",
      contractType:
        partner === renewedContract.contractSellers[0] ? ContractTradeTypeEnum.BUY : ContractTradeTypeEnum.SELL,
      contactEmail: renewedContract?.contact?.email ?? "",
      contactPhone: renewedContract?.contact?.phone ?? "",
      counterpartyContactEmail: renewedContract?.counterpartyContact?.email ?? "",
      counterpartyContactPhone: renewedContract?.counterpartyContact?.phone ?? "",
      selectFromExisting: true,
      kgPerM3: contractProduct?.kgPerM3 || 0,
      kgPerStk: contractProduct?.kgPerStk || 0,
      kgPerMeter: contractProduct?.kgPerMeter || 0,
      companySpecificServicesOptions: companySpecificServicesDB ?? [],
      isPaymentReceiverInverted:
        renewedContract.agreedBuyerPricePrUnit < 0 || renewedContract.agreedSellerPricePrUnit < 0,
      automaticSalesApproval: renewedContract.automaticSalesApproval ? "YES" : "NO",
      template: renewedContract.template ? "YES" : "NO",
      templateName: renewedContract.templateName ?? null,
      contractDeviations: renewedContract.contractDeviations,
      contractDeviationOptions: [],
      poNumber: renewedContract.poNumber ?? null,
      attachmentUUIDs: renewedContract.attachmentUUIDs ?? [],
      skipInvoicing: renewedContract.skipInvoicing,
      indexRegulated: renewedContract.indexRegulated,
    };
    onChangeCreateContractValues(convertedObj);
  }, [renewedContract, currentCompany?.companyId, companySpecificServicesDB, paymentOptions]);

  useEffect(() => {
    initiateContract();
  }, [paymentOptions, companySpecificServicesDB]);

  function initiateContract() {
    if (renewedContract) return;
    /*const serviceFeeId = paymentOptions?.settings.find((s) => s.name === "servicefee")?.id ?? 0;

    const defaultServices: number[] = paymentDefaultsForCompany
      ? [...paymentDefaultsForCompany.settings.map((s) => s.id), serviceFeeId]
      : [serviceFeeId]; */

    onChangeCreateContractValues({
      ...CreateNewContractPlaceholderObj,
      companySpecificServicesOptions: companySpecificServicesDB,
      contractDeviationOptions: contractDeviations ? contractDeviations : [],
      invoiceDueDateId:
        paymentDefaultsForCompany && paymentDefaultsForCompany?.dueDate
          ? paymentDefaultsForCompany.dueDate
          : { id: 1, days: 7 },
      invoiceIntervalId:
        paymentDefaultsForCompany && paymentDefaultsForCompany?.interval ? paymentDefaultsForCompany.interval?.id : 1,
      currencyType: currentCompany?.companyCurrency,
    });
  }
  const { mutate: archiveContractMutation } = useMutation({
    mutationFn: async (contractId: number) => {
      await archiveContract(contractId as number);
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:contracts.contractArchived"))
      );
    },
    onError: (_) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:contracts.contractArchivedError"))
      );
    },
  });

  const { mutate: mutateCreateContract, isPending: isPendingMutateCreateContract } = useMutation({
    mutationFn: async (data: CreateContract) => {
      const req = {
        ...data,
        previousContractId: renewedContract?.previousContractId || null,
        amount: data.contractEndDate !== null ? null : data.amount,
      };
      await postCreateContract(req, taskId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getContractsLight"] });
      await queryClient.invalidateQueries({
        queryKey: ["getContractById", contractId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:contracts.contractCreated"),
          "create-contract-success"
        )
      );
      await queryClient.invalidateQueries({ queryKey: ["getContractById", contractId] });
      await queryClient.invalidateQueries({ queryKey: ["getOpenTasks", currentCompany?.companyId] });
      // archive the old contract if there is a renewContract
      if (renewedContract && !!renewedContract.previousContractId && !isArchived) {
        archiveContractMutation(renewedContract.previousContractId);
      }
      setIsSubmitting(false);
      navigate("/contracts");
      closeWizard();
    },
    onError: () => {
      setIsSubmitting(false);
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:contracts.contractCreatedError"))
      );
    },
  });

  const onChangeCreateContractValues = (changed: Partial<CreateContractWizard>) => {
    setCreateContractValues((prev) => ({ ...prev, ...changed }));
  };

  const validateCurrentStep = () => {
    if (isSubmitting) return;
    const form = formSections.current[currentStep];
    if (form) {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    }
  };

  const onFormSectionValidationSuccess = () => {
    if (!currentCompany?.companyId || isSubmitting) return;
    if (currentStep === steps - 1) {
      setIsSubmitting(true);
      const newContract = ConvertFormToContract(createContractValues, currentCompany?.companyId);
      mutateCreateContract(newContract);
    } else {
      next();
    }
  };

  function closeWizard() {
    // initiateContract();
    setCreateContractValues(CreateNewContractPlaceholderObj);
    setCurrentStep(0);
    // setSteps(initialSteps(5));
    close();
  }

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={closeWizard}
        size="xl"
        closeOnClickOutside={false}
        showCloseButton={true}
        title={
          <ModalHeader testId="create-contract-modal-header" title={t("contracts:newContract")} svgAsset="contract" />
        }
      >
        <Stepper
          active={currentStep}
          onStepClick={setCurrentStep}
          allowNextStepsSelect={false}
          classNames={{
            stepIcon: styles.stepIcon,
            step: styles.step,
            separator: styles.separator,
            steps: styles.stepperHeader,
            stepLabel: styles.stepLabel,
            content: styles.stepperContent,
          }}
          size="sm"
          completedIcon={<SVGAsset name="step-complete" />}
        >
          <Stepper.Step
            label={t("contracts:details")}
            icon={
              <Tooltip label={t("contracts:details")}>
                <span>1</span>
              </Tooltip>
            }
          >
            <ContractDetails
              ref={useIndexedRef(formSections, 0)}
              onValidationSuccess={onFormSectionValidationSuccess}
              onChangeWizardData={onChangeCreateContractValues}
              wizardData={createContractValues}
            />
          </Stepper.Step>

          <Stepper.Step
            label={t("common:settings")}
            icon={
              <Tooltip label={t("common:settings")}>
                <span>2</span>
              </Tooltip>
            }
          >
            <ContractSettings
              ref={useIndexedRef(formSections, 1)}
              onValidationSuccess={onFormSectionValidationSuccess}
              onChangeWizardData={onChangeCreateContractValues}
              wizardData={createContractValues}
            />
          </Stepper.Step>

          <Stepper.Step
            label={t("contracts:invoicing")}
            icon={
              <Tooltip label={t("contracts:invoicing")}>
                <span>3</span>
              </Tooltip>
            }
          >
            <Payment
              ref={useIndexedRef(formSections, 2)}
              onValidationSuccess={onFormSectionValidationSuccess}
              onChangeWizardData={onChangeCreateContractValues}
              wizardData={createContractValues}
            />
          </Stepper.Step>
          <Stepper.Step
            label={t("common:deviations")}
            icon={
              <Tooltip label={t("common:deviations")}>
                <span>4</span>
              </Tooltip>
            }
          >
            <Deviations
              ref={useIndexedRef(formSections, 3)}
              onValidationSuccess={onFormSectionValidationSuccess}
              onChangeWizardData={onChangeCreateContractValues}
              wizardData={createContractValues}
            />
          </Stepper.Step>

          <Stepper.Step
            label={t("contracts:transport")}
            icon={
              <Tooltip label={t("contracts:transport")}>
                <span>5</span>
              </Tooltip>
            }
          >
            <Transport
              ref={useIndexedRef(formSections, 4)}
              onValidationSuccess={onFormSectionValidationSuccess}
              onChangeWizardData={onChangeCreateContractValues}
              wizardData={createContractValues}
            />
          </Stepper.Step>
          <Stepper.Step
            label={t("contracts:attachments")}
            icon={
              <Tooltip label={t("contracts:attachments")}>
                <span>6</span>
              </Tooltip>
            }
          >
            <Attachments
              ref={useIndexedRef(formSections, 5)}
              onValidationSuccess={onFormSectionValidationSuccess}
              onChangeWizardData={onChangeCreateContractValues}
              wizardData={createContractValues}
            />
          </Stepper.Step>
        </Stepper>

        <ModalCTAs>
          <CTA data-testid="cancel-create-contract" intent="secondary" onClick={() => closeWizard()}>
            {t("actions:cancel")}
          </CTA>

          {currentStep > 0 && (
            <CTA data-testid="back-create-sales-order" intent="secondary" onClick={prev}>
              {t("actions:back")}
            </CTA>
          )}

          <CTA
            id="next-create-sales-order"
            intent="primary"
            data-testid="create-contract-next-button"
            onClick={validateCurrentStep}
            loading={isPendingMutateCreateContract || isSubmitting}
            disabled={isSubmitting}
          >
            {currentStep !== ContractStepTypeEnum.ATTACHMENT ? t("actions:next") : t("actions:create")}
          </CTA>
        </ModalCTAs>
      </Modal>
    </>
  );
}
